/**
 * The pieces every control's schema is made of (D354, "Apps: every HeroUI control"). A control is one node type of `UiSpecV1`
 * (shared/schemas/uiSpec.ts): a closed zod object, strict like the first eighteen, whose strings and numbers are bounded and
 * whose data comes in as literal `rows` or as `rowsFrom`, a binding's list. A control never carries code, markup, a style or a
 * URL; a press on it is one of the closed {@link UiAction}s.
 *
 * Renderer-neutral on purpose: nothing here names a web-only prop. The web renderer draws a control with the HeroUI component
 * its definition names; AIOE's phone app draws the same node with HeroUI Native or the fallback `shared/apps/phoneControls.ts`
 * declares.
 *
 * OURS: the workbench defines these files; AIOE mirrors them with the header line, as it does `uiSpec.ts`.
 */
import { z } from "zod";
import { UI_LIMITS, UI_TONES, uiActionSchema } from "../uiBase.js";
/** The groups the gallery and `ui_schema` list the controls under. */
export const CONTROL_GROUPS = ["data-display", "charts", "forms", "navigation", "overlays", "map"];
export const CONTROL_GROUP_TITLES = {
    "data-display": "Data display",
    charts: "Charts and data visualisation",
    forms: "Forms",
    navigation: "Navigation inside an app",
    overlays: "Overlays (behaviour, not free-standing)",
    map: "Map",
};
const label = z.string().max(UI_LIMITS.label);
const hint = z.string().max(UI_LIMITS.hint);
const tone = z.enum(UI_TONES);
/** A binding path as a spec names it: `tasks.items`, `prs`. */
export const bindingPath = z.string().regex(/^[a-z][a-z0-9_]{0,39}(\.[A-Za-z0-9_]{1,40}){0,6}$/, "a binding path like tasks.items");
/** A key of a row: `owner`, `due_date`. */
export const rowKey = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,31}$/, "a column key like owner");
export const fieldName = z.string().regex(/^[a-z][a-z0-9_]{0,31}$/, "a field name is lower-case letters, digits and underscores, starting with a letter");
const cell = z.union([z.string().max(UI_LIMITS.label * 2), z.number(), z.boolean(), z.null()]);
const option = z.object({ value: z.string().min(1).max(60), label, description: hint.optional(), icon: z.string().max(40).optional(), tone: tone.optional() }).strict();
const button = z.object({ label: label.min(1), action: uiActionSchema, variant: z.enum(["primary", "secondary", "danger"]).optional(), tone: tone.optional() }).strict();
/** The kit for one schema build: `node` is the (lazy) schema of any node, for containers. */
export function makeKit(node) {
    const nodes = (max = UI_LIMITS.children) => z.array(z.lazy(node)).max(max);
    return {
        z,
        label,
        hint,
        tone,
        name: fieldName,
        key: rowKey,
        path: bindingPath,
        cell,
        action: uiActionSchema,
        nodes,
        rows: (max = UI_LIMITS.tableRows) => z.array(z.record(rowKey, cell)).max(max).optional(),
        rowsFrom: bindingPath.optional(),
        keys: z.record(z.string().max(32), rowKey).optional(),
        option,
        options: (max = UI_LIMITS.controlOptions) => z.array(option).min(1).max(max),
        button,
        num: z.number().finite(),
        text: z.string().max(UI_LIMITS.text),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "a colour like #3b82f6"),
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "a date like 2026-10-25"),
        time: z.string().regex(/^\d{2}:\d{2}$/, "a time like 14:30"),
        numbers: (max = UI_LIMITS.chartRows) => z.array(z.number().finite()).max(max),
        panels: (max = UI_LIMITS.panels) => z
            .array(z.object({ id: z.string().regex(/^[a-z][a-z0-9_-]{0,31}$/, "a panel id like details"), label, children: nodes() }).strict())
            .min(1)
            .max(max),
    };
}
/** Marks a definition (a place to hang a check on later without touching every file). */
export function control(def) {
    return def;
}
//# sourceMappingURL=kit.js.map